import { useEffect, type ReactNode } from "react";

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  maxWidth?: number;
};

export default function Modal({
  open,
  onClose,
  title,
  children,
  maxWidth = 520,
}: Props) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(18, 63, 56, 0.55)",
        zIndex: 100,
        display: "grid",
        placeItems: "center",
        padding: "1rem",
        overflowY: "auto",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth,
          background: "var(--c-sand)",
          borderRadius: "var(--r-lg)",
          boxShadow: "var(--shadow-card)",
          padding: "clamp(1.25rem, 4vw, 1.75rem)",
          maxHeight: "calc(100vh - 2rem)",
          overflowY: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "start",
            justifyContent: "space-between",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          <h2
            className="display"
            style={{
              fontSize: "clamp(1.4rem, 3.5vw, 1.85rem)",
              margin: 0,
              lineHeight: 1.1,
            }}
          >
            {title.toLowerCase()}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 36,
              height: 36,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--c-palm)",
              borderRadius: "var(--r-sm)",
              background: "transparent",
              flex: "0 0 auto",
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
